import type { Pass } from '@/types/passes';
import type { ProPlanAnnualOffer } from '@/types/checkout';

/**
 * The canonical product id a Pass / Pro Plan row is sold under on the web rail.
 * It is planted on the purchase intent at checkout and used again afterwards to
 * find the same row in `GET /v1/passes` while waiting for the webhook grant, so
 * both sides must derive it identically — hence one helper.
 */
export function passCheckoutSku(pass: Pass): string {
  return (
    pass.attributes.provider_google_product_id ??
    pass.attributes.provider_apple_product_id ??
    pass.id
  );
}

/**
 * Same rule for the yearly Pro Plan (`annual_offer`). The annual row is not
 * listed in `GET /v1/passes` on its own: its grant shows up as the monthly
 * `pro_plan` row turning owned, so the grant poll matches a pass by either SKU.
 */
export function annualOfferSku(offer: ProPlanAnnualOffer): string {
  return offer.provider_google_product_id ?? offer.provider_apple_product_id ?? offer.id;
}

/** Every SKU a purchase of this pass row can be planted under. */
export function passCheckoutSkus(pass: Pass): string[] {
  const annual = pass.attributes.annual_offer;
  return annual ? [passCheckoutSku(pass), annualOfferSku(annual)] : [passCheckoutSku(pass)];
}
